import { ChevronsRightIcon, Download, Minus, Plus } from "lucide-react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";

import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { fetchData } from "../../../utils/functions";
import PermissionButton from "../../common/PermissionButton";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import Loader from "../../partials/Loader";
import "font-awesome/css/font-awesome.min.css";

import GeneralFooter from "../../common/GeneralFooter";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [pendingGroup, setPendingGroup] = useState("");
  const [pendingRole, setPendingRole] = useState("");
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);

  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const [loader, setLoader] = useState(true);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetchData(
        "groups",
        "GET",
        null,
        {},
        { limit: 10000 },
        navigate,
        "/users",
        dispatch
      );
      if (response?.data) {
        setGroups(response.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  }, [navigate, dispatch]);
  const fetchRoles = useCallback(async () => {
    const groupIdToSend = pendingGroup || undefined; 

    try {
      const response = await fetchData(
        "role",
        "GET",
        null,
        {},
        { limit: 10000, group_id: groupIdToSend },
        navigate,
        "/users",
        dispatch
      );

      if (response?.data && Array.isArray(response.data)) {
        setRoles(response.data);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  }, [navigate, dispatch, pendingGroup]); 

  const fetchUsers = async () => {
    setLoader(true);
    try {
      const response = await fetchData(
        "user",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          group_id: selectedGroup || undefined,
          role_id: selectedRole || undefined,
          exclude_self: 1,
        },
        navigate,
        "/users",
        dispatch
      );
      setLoader(false);
      const usersData = response?.data || [];
      setUsers(usersData);
      setPagination(response?.pagination || pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoader(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await fetchData(
        "user",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: 1,
          limit: 10000,
          group_id: selectedGroup || undefined,
          role_id: selectedRole || undefined,
          exclude_self: 1,
        },
        navigate,
        "/users",
        dispatch
      );
      const allUsers = response?.data || [];

      if (!allUsers.length) {
        toast.warning("No users to export");
        return;
      }
      const excelData = allUsers.map((user, index) => ({
        "Sr. No": index + 1,
        Name: user.name || "",
        Email: user.email || "",
        CNIC: user.cnic || "",
        "Phone Number": user.phone_number || "",
        Role: user.role_name || "",
        Group: user.group?.name || "",
        "User Code": user.user_code || "",
        Status: user.status === 1 ? "Active" : "Inactive",
        "Created At": user.createdAt
          ? new Date(user.createdAt).toLocaleDateString()
          : "",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      XLSX.writeFile(
        workbook,
        `users_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      toast.success(`Successfully exported ${allUsers.length} users to Excel`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to export users to Excel");
    } finally {
      setIsExporting(false);
    }
  };
  const showDeleteModal = (uuid) => {
    setSelectedUuid(uuid);
    setModalVisible(true);
  };
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setModalVisible(false);

    try {
      const response = await fetchData(
        "user",
        "DELETE",
        { id: selectedUuid },
        {},
        {},
        navigate,
        "/user",
        dispatch
      );

      if (response?.success || response?.message?.includes("successfully")) {
        setUsers((prev) => prev.filter((u) => u.id !== selectedUuid));
        toast.success("User deleted successfully.");
      } else {
        throw new Error(response?.message || "Failed to delete the user.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Error deleting user.");
    } finally {
      setIsDeleting(false);
      setSelectedUuid(null);
    }
  };
  useEffect(() => {
    fetchGroups();
    setPendingGroup(selectedGroup);
    setPendingRole(selectedRole);
  }, [fetchGroups]);
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (search && pagination.currentPage !== 1) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }
    const handler = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [
    pagination.currentPage,
    pagination.pageSize,
    search,
    selectedGroup,
    selectedRole,
  ]);

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };
  const handlePendingGroupChange = (value) => {
    setPendingGroup(value);
    setPendingRole(""); 
  };

  const handlePendingRoleChange = (value) => {
    setPendingRole(value);
  };
  const roleOptions = useMemo(() => {
    if (!roles || roles.length === 0) {
      return [];
    }

    return roles.reduce((acc, roleItem) => {
      const roleData = roleItem.role || roleItem;
      const id = roleData.id || roleData.role_id;
      const roleName = roleData.role_name || roleData.name;

      if (id && roleName) {
        acc.push({ value: id, label: roleName });
      }
      return acc;
    }, []);
  }, [roles]);

  const handleApplyFilters = () => {
    if (pendingGroup !== selectedGroup || pendingRole !== selectedRole) {
      setSelectedGroup(pendingGroup);
      setSelectedRole(pendingRole);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      toast.success("Filters applied successfully. Loading data...");
    } else {
      toast.info("No filter changes to apply.");
    }
  };
  const clearFilters = () => {
    setSelectedGroup("");
    setSelectedRole("");
    setPendingGroup("");
    setPendingRole("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    toast.success("Filters cleared. Loading data...");
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
    setPendingGroup(selectedGroup);
    setPendingRole(selectedRole);
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const columns = [
    {
      name: <span className="text-[0.819rem] uppercase">Name</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Email</span>,
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Group</span>,
      selector: (row) => row.group?.name || "-",
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Role</span>,
      selector: (row) => row.role_name || "-",
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Status</span>,
      cell: (row) => (
        <span
          className={`py-1 px-2 text-[0.85em] rounded ${
            row.status === 1 ? "bg-success text-white" : "bg-red-500 text-white"
          }`}
        >
          {row.status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];
  if (
    permissions.includes("user-update") ||
    permissions.includes("user-delete")
  ) {
    columns.push({
      name: <span className="text-[0.819rem] uppercase">Action</span>,
      cell: (row) => (
        <div className="flex justify-center">
          <PermissionButton
            hasPermission={permissions.includes("user-update")}
            to={`/users/edit/${row.id}`}
            faIcon="fa-solid fa-pen-to-square fa-lg"
            onlyIcon={true}
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed"
          />
          <PermissionButton
            hasPermission={permissions.includes("user-delete")}
            faIcon="fa fa-trash fa-lg text-primaryDark"
            onlyIcon
            onClick={() => showDeleteModal(row.id)}
            className="p-2 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
          />
        </div>
      ),
    });
  }

  return (
    <div className="my-4 w-full flex flex-col justify-between items-center">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Manage Users
        </h1>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
              isExporting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Download size={16} />
            {isExporting ? "Exporting..." : "Export Excel"}
          </button>
          <PermissionButton
            hasPermission={permissions.includes("user-create")}
            to="/users/add"
            faIcon="fa fa-plus"
            text="Add"
            className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
            enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
            disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
          />
        </div>
      </div>
      <Accordion
        expanded={isFiltersVisible}
        className="w-full mt-4 rounded-lg shadow"
      >
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#ffff" size={14} />
            ) : (
              <Plus size={14} color="#ffff" />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          onClick={toggleFilters}
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important",
            "& .MuiAccordionSummary-content": {
              color: "#fff  !important",
            },
          }}
        >
          Filters
        </AccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-4 w-full pt-4">
            <div className="flex flex-col-4 gap-4 items-center">
              <ThinDropdownWithSearch required={false}
                label="Filter by Group"
                id="group-dropdown"
                options={groups.map((g) => ({
                  value: g.id,
                  label: g.name,
                }))}
                value={pendingGroup}
                onChange={handlePendingGroupChange}
              />
              <ThinDropdownWithSearch required={false}
                label="Filter by Role"
                id="role-dropdown"
                options={roleOptions}
                value={pendingRole}
                onChange={handlePendingRoleChange}
              />
              <div className="flex gap-3 items-end mt-5">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-[#be9609] transition-colors font-medium"
                >
                  Clear
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white text-white rounded hover:bg-green-700 transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <div className="rounded-lg mb-[30px] mt-4 shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Users List
          </h1>
        </div>

        <div className="p-[1.25rem] w-full">
          <div className="flex flex-col lg:flex-row gap-4 mb-[6px] w-full items-start lg:items-center justify-between">
            <div className="flex gap-3 items-center">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
            </div>

            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              placeholder="Search..."
              type="search"
              className="font-[Light] text-[#212529] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>

          <DataTable
            columns={columns}
            data={users}
            paginationTotalRows={pagination.totalItems}
            customStyles={customStyles}
            highlightOnHover
          />

          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={users.length}
          />
        </div>
      </div>

      <CustomConfirmModal
        isVisible={isModalVisible}
        onConfirm={handleDelete}
        onCancel={() => setModalVisible(false)}
        message="Are you sure you want to delete this user?"
      />
    </div>
  );
};

export default Users;
